/**
 * Builds a new quotation payload with updated member benefits
 * @param {Object} policy - The current policy object
 * @param {Array} updatedMembers - Members with updated benefits
 * @returns {Object} - New quotation payload ready for API
 */
export function buildQuotationWithUpdatedBenefits(policy, updatedMembers) {
  // Build policy_schedule_entries from updated members
  const policy_schedule_entries = updatedMembers.map(member => ({
    guid: member.guid,
    first_name: member.first_name,
    middle_name: member.middle_name || '',
    last_name: member.last_name,
    msisdn: member.msisdn || member.mobile_number,
    date_of_birth: member.date_of_birth,
    gender: member.gender,
    national_id: member.national_id || member.id_number || 'N/A',
    passport: member.passport || 'N/A',
    postal_address: member.postal_address || '',
    relationship: member.relationship,
    residential_address: member.residential_address || '',
    type: member.type || 'Nuclear',
    premium_amount_in_cents: member.premium_amount_in_cents || 0,
    extended_premium: member.extended_premium || 0,
    add_on_premium: member.add_on_premium || 0,
    benefits: member.benefits || [],
    uuid: member.uuid,
    additional_details: member.additional_details || {}
  }));

  // Preserve extended_members_benefit for Extended_2 members
  const existingExtendedBenefit = policy.quotation && policy.quotation.additional_details
    ? policy.quotation.additional_details.extended_members_benefit
    : null;

  return {
    product_guid: policy.product_guid,
    product_name: policy.product_name,
    product_code: policy.product_code,
    branch: policy.branch || 'string',
    customer_guid: policy.customer_guid,
    partner: policy.partner,
    lapse_period: policy.lapse_period || 12,
    lapse_period_granularity: policy.lapse_period_granularity || 'month',
    lapse_period_cardinality: policy.lapse_period_cardinality || 1,
    premium_granularity: policy.premium_granularity || 'month',
    premium_cardinality: policy.premium_cardinality || 1,
    policy_schedule_entries: policy_schedule_entries,
    additional_details: {
      ...policy.quotation.additional_details,
      primary_plan: (policy.quotation && policy.quotation.additional_details && policy.quotation.additional_details.primary_plan) || null,
      secondary_plan: (policy.quotation && policy.quotation.additional_details && policy.quotation.additional_details.secondary_plan) || null,
      cover_type: (policy.quotation && policy.quotation.additional_details && policy.quotation.additional_details.cover_type) || 'Member & Family',
      extended_members_benefit: existingExtendedBenefit
    }
  };
}

/**
 * Validates that all members have at least one benefit
 * Note: Core benefits (Funeral, Accidental Death) are always included from the plan
 * This validation just ensures the benefits array is not empty
 * @param {Array} members - Members to validate
 * @returns {Object} - { valid: boolean, errors: Array }
 */
export function validateMemberBenefits(members) {
  const errors = [];

  members.forEach(member => {
    const benefits = member.benefits || [];

    if (benefits.length === 0) {
      errors.push(`${member.first_name} ${member.last_name} must have at least one benefit`);
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
}
